import React, { FC, useState, useMemo } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import urlJoin from 'url-join';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import GET_POLICY_LIST from './getPolicyList.graphql';
import { Formik } from 'formik';
import { Asset, FilterIcon, Pill } from '@clearcover/atlas';
import { Table, EmptyTable } from 'Shared/components/Table';
import { FormInput } from 'Shared/components/FormInput';
import { useSort, SortDirectionType } from 'Shared/components/Table/hooks';
import {
  STATE_OPTIONS,
  INDEPENDENT_AGENT_STATES,
} from 'Shared/constants/states';
import { AnimatedScreen } from 'Shared/components/AnimatedScreen';
import { Paginate } from 'Shared/components/Paginate';
import {
  PolicyListType,
  PolicyListScopeType,
  HeaderType,
  GetSortPayloadType,
  PolicyListSortPayloadType,
  PolicyListQueryParamsType,
  PolicyListFilterPayloadType,
} from './types';
import { POLICY_STATUS_COLOR_MAP } from 'Policies/utils';
import { isPresent, formatDateShort } from 'Shared/utils';
import { useAuth0 } from 'Auth';
import { Can } from 'Shared/utils/auth';
import { viewSystemPolicies, viewPolicyScopeToggle } from 'Admin/authPolicies';
import { RadioGroup } from 'Shared/components/RadioGroup';

import styles from './styles.module.css';
import { useFilters } from 'Shared/components/Table/hooks';
import { EmptyFilterTable } from 'Shared/components/Table/Table';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import { useTitle } from 'Shared/hooks';

const getPolicySortPayload: GetSortPayloadType = (field, direction) => ({
  policy: { field, direction },
});

const getPolicyHolderSortPayload: GetSortPayloadType = (field, direction) => ({
  policyHolder: { field, direction },
});

/*
  If we find out that all sort payloads will conform to the same payload structure,
  we should refactor this to not be a function, and move the structure into
  the generic useSort functionality.
*/
const headers: Array<HeaderType> = [
  {
    title: 'Policy',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicySortPayload('number', direction),
  },
  {
    title: 'Status',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicySortPayload('status', direction),
  },
  {
    title: 'First Name',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicyHolderSortPayload('firstName', direction),
  },
  {
    title: 'Last Name',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicyHolderSortPayload('lastName', direction),
  },
  {
    title: 'Email',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicyHolderSortPayload('email', direction),
  },
  {
    title: 'Phone',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicyHolderSortPayload('phone', direction),
  },
  {
    title: 'State',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicySortPayload('state', direction),
  },
  {
    title: 'Effective Date',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicySortPayload('effectiveDate', direction),
  },
  {
    title: 'Expiration Date',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicySortPayload('expirationDate', direction),
  },
  {
    title: 'Updated Date',
    getSortPayload: (direction: SortDirectionType) =>
      getPolicySortPayload('updatedAt', direction),
  },
  {
    title: '', // view link
  },
];

const searchFilters = [
  {
    field: 'firstName',
    getFilterPayload: (text: Array<string>) => (text[0] || '').toLowerCase(),
  },
  {
    field: 'lastName',
    getFilterPayload: (text: Array<string>) => (text[0] || '').toLowerCase(),
  },
  {
    field: 'email',
    getFilterPayload: (text: Array<string>) => text[0],
  },
  {
    field: 'phone',
    getFilterPayload: (text: Array<string>) =>
      (text[0] || '').replace(/\D/g, ''),
  },
];

const policyNumberFilter = {
  field: 'policyNumber',
  getFilterPayload: (text: Array<string>) => text[0],
};

export const POLICY_RECORDS_PER_PAGE = 25;

export const PolicyList: FC<RouteComponentProps> = ({ match }) => {
  useTitle('Policy List');

  const { user } = useAuth0();
  const [resultsPerPage, changeResultsPerPage] = useState(
    POLICY_RECORDS_PER_PAGE
  );

  const {
    activeFilters,
    hasActiveFilters,
    addValueToFilter,
    removeValueFromFilter,
    modifyMultipleFilters,
    clearAllFilters,
  } = useFilters({}, true);

  let newScope: PolicyListScopeType = {
    entity: 'USER',
    id: user!.id,
  };
  if (viewSystemPolicies(user!.effectiveGrants)) {
    newScope = {
      entity: 'SYSTEM',
    };
  } else if (isPresent(user!.partner)) {
    newScope = {
      entity: 'PARTNER',
      id: user!.partner.id,
    };
  }

  const filtersToMultiSelectItems = (
    fieldName: string,
    availableFilters: Array<{ display: string; value: string }>
  ) => {
    const activeFiltersForField = activeFilters[fieldName] || [];
    return availableFilters.map(({ display, value }) => ({
      key: value,
      label: display,
      selected: activeFiltersForField.includes(value),
    }));
  };

  const [scope, updateScope] = useState<PolicyListScopeType>(newScope);
  const { sort, tableHeaders } = useSort<PolicyListSortPayloadType>(headers, {
    title: 'Updated Date',
    direction: 'desc',
    payload: { policy: { field: 'updatedAt', direction: 'desc' } },
  });

  const multiSelectFilters = useMemo(
    () => [
      {
        field: 'State',
        availableFilters: INDEPENDENT_AGENT_STATES.map(state => ({
          display: `${state} - ${STATE_OPTIONS[state]?.toUpperCase()}`,
          value: state,
        })),
        getFilterPayload: (selectedFilters: Array<string>) => {
          const selected = Array.from(selectedFilters);
          return {
            ...(selected.length && { state: selected }),
          };
        },
      },
      {
        field: 'Status',
        availableFilters: Object.keys(
          POLICY_STATUS_COLOR_MAP
        ).map((label: string) => ({ display: label, value: label })),
        getFilterPayload: (selectedFilters: Array<string>) => {
          const selected = Array.from(selectedFilters);

          return {
            ...(selected.length && {
              statusSet: selected.map(filter => {
                return { displayStatus: filter };
              }),
            }),
          };
        },
      },
    ],
    []
  );

  const filters = useMemo<PolicyListFilterPayloadType>(() => {
    return {
      policy: multiSelectFilters.reduce((acc, filter) => {
        const isFilterActive = isPresent(activeFilters[filter.field]);

        return isFilterActive
          ? {
              ...acc,
              ...filter.getFilterPayload(activeFilters[filter.field]),
            }
          : acc;
      }, {}),
      policyHolder: searchFilters.reduce((acc, filter) => {
        const isFilterActive = isPresent(activeFilters[filter.field]);

        return isFilterActive
          ? {
              ...acc,
              [filter.field]: filter.getFilterPayload(
                activeFilters[filter.field]
              ),
            }
          : acc;
      }, {}),
      policyNumber: isPresent(activeFilters[policyNumberFilter.field])
        ? (policyNumber => {
            return policyNumber.length >= 3 ? policyNumber : undefined;
          })(
            policyNumberFilter.getFilterPayload(
              activeFilters[policyNumberFilter.field]
            )
          )
        : undefined,
    };
  }, [activeFilters, multiSelectFilters]);

  const variables = {
    scope,
    sort,
    paginate: {
      limit: resultsPerPage,
      offset: 0,
    },
    filters,
  };

  const { data, error, networkStatus, fetchMore } = useQuery<
    PolicyListType,
    PolicyListQueryParamsType
  >(GET_POLICY_LIST, {
    variables,
    fetchPolicy: 'network-only', // need to do some investigation prior to changing this.
    notifyOnNetworkStatusChange: true,
  });

  const policies = data && data.listPolicies && data.listPolicies.policies;
  const pagination = data && data.listPolicies && data.listPolicies.pagination;

  const handleSearchSubmit = (data: { [field: string]: string }) => {
    modifyMultipleFilters(
      Object.keys(data).reduce((acc, key) => {
        return { ...acc, [key]: [data[key]] };
      }, {})
    );
  };

  const viewLink = (policyId: string) => (
    <Link to={urlJoin(match.url, policyId)} className={styles.viewLink}>
      View
    </Link>
  );

  return (
    <AnimatedScreen
      testId="policy-list"
      error={error && error.message}
      loading={networkStatus === 1}
    >
      <h1>Policies</h1>
      <Formik
        enableReinitialize
        initialValues={{
          firstName: activeFilters.firstName?.[0] || '',
          lastName: activeFilters.lastName?.[0] || '',
          email: activeFilters.email?.[0] || '',
          phone: activeFilters.phone?.[0] || '',
          policyNumber: activeFilters.policyNumber?.[0] || '',
        }}
        onSubmit={handleSearchSubmit}
      >
        {({ values, handleChange, handleSubmit }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={styles.search}
              data-testid="search-form"
            >
              <FormInput
                role="search"
                variant="search"
                name="policyNumber"
                onChange={handleChange}
                value={values.policyNumber}
                label="Search policy number"
                data-testid="policy-number"
                onIconClick={() => {
                  handleSubmit();
                }}
              />

              <FormInput
                role="search"
                variant="search"
                name="firstName"
                onChange={handleChange}
                value={values.firstName}
                label="Search first name"
                data-testid="first-name"
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                role="search"
                variant="search"
                name="lastName"
                onChange={handleChange}
                value={values.lastName}
                label="Search last name"
                data-testid="last-name"
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                role="search"
                variant="search"
                name="email"
                onChange={handleChange}
                value={values.email}
                label="Search email address"
                labelVariant="floating"
                data-testid="email-address"
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <FormInput
                role="search"
                variant="search"
                name="phone"
                onChange={handleChange}
                value={values.phone}
                label="Search phone number"
                data-testid="phone-number"
                onIconClick={() => {
                  handleSubmit();
                }}
              />
              <button type="submit" style={{ display: 'none' }}>
                submit
              </button>
            </form>
          );
        }}
      </Formik>
      <div className={styles.filters}>
        <span className={styles.filterHeader}>
          Filters
          <Asset
            className={styles.filterIcon}
            icon={FilterIcon}
            height={16}
            width={16}
            description="Filter"
          />
        </span>
        {multiSelectFilters.map(f => (
          <FormInput
            key={f.field}
            variant="multiselect"
            items={filtersToMultiSelectItems(f.field, f.availableFilters)}
            label={f.field}
            data-testid={`${f.field.toLowerCase()}-multiselect`}
            onChangeSelection={item => {
              if (item) {
                item.selected
                  ? removeValueFromFilter(f.field, item.key)
                  : addValueToFilter(f.field, item.key);
              }
            }}
          />
        ))}
        <ClearFilterButton
          onClick={clearAllFilters}
          disabled={!hasActiveFilters}
          data-testid={'clear-filters'}
        >
          Clear Filters And Search
        </ClearFilterButton>
        <Can
          policy={viewPolicyScopeToggle}
          grants={user!.effectiveGrants}
          yes={() => (
            <RadioGroup
              className={styles.filterOwnPolicies}
              name="policyFilter"
              variant="inline"
              options={[
                { label: 'Show only my policies', value: 'USER' },
                { label: 'Show all policies in my agency', value: 'PARTNER' },
              ]}
              value={scope!.entity}
              onChange={(value: string) => {
                if (value === 'USER') {
                  updateScope({
                    entity: 'USER',
                    id: user!.id,
                  });
                } else if (value === 'PARTNER') {
                  updateScope({
                    entity: 'PARTNER',
                    id: user!.partner!.id,
                  });
                }
              }}
            />
          )}
        />
      </div>

      {policies && policies.length ? (
        <>
          <Table
            loading={[2, 3, 4].includes(networkStatus)}
            headers={tableHeaders}
            name="policy-list-table"
            columnWidths={[1, 1, 1, 1, 2, 1, '60px', 1, 1, 1, '60px']}
            columnDataTestIdPrefix={[
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              'view-link',
            ]}
            rows={policies.map(policy => {
              const phone =
                policy.policyHolder.phone &&
                parsePhoneNumberFromString(policy.policyHolder.phone, 'US');

              return {
                key: policy.id,
                data: [
                  <span className={styles.policyNumberCell}>
                    {policy.number}
                  </span>,

                  <Pill
                    variant={
                      POLICY_STATUS_COLOR_MAP[policy.displayStatus] ||
                      'disabled'
                    }
                    className={styles.policyStatus}
                    title={policy.displayStatus}
                  >
                    {policy.displayStatus}
                  </Pill>,
                  policy.policyHolder.firstName,
                  policy.policyHolder.lastName,
                  policy.policyHolder.email,
                  phone && phone.formatNational(),
                  policy.state,
                  formatDateShort(policy.effectiveDate),
                  formatDateShort(policy.expirationDate),
                  formatDateShort(policy.updatedAt),
                  { component: viewLink(policy.policyId), allowOverflow: true },
                ],
              };
            })}
            hasPagination
          />
          <Paginate
            total={pagination?.total || 0}
            offset={pagination?.offset}
            limit={pagination?.limit}
            onOffsetChange={(limit, offset) => {
              fetchMore({
                variables: { paginate: { offset, limit } },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return Object.assign({}, prev, {
                    listPolicies: {
                      ...prev.listPolicies,
                      ...fetchMoreResult.listPolicies,
                    },
                  });
                },
              });
            }}
            onLimitChange={changeResultsPerPage}
          />
        </>
      ) : activeFilters ? (
        <EmptyFilterTable id={'no-matched-policies'}>
          <div>No policies were found matching your filters</div>
          <ul>
            Tips:
            <li>Check your spelling</li>
            <li>Filtering only works on exact matches</li>
          </ul>
        </EmptyFilterTable>
      ) : (
        <EmptyTable>
          <div>There are no policies that you can view.</div>
          <div>
            You can <Link to="/quotes">View Quotes</Link>.
          </div>
        </EmptyTable>
      )}
    </AnimatedScreen>
  );
};
